var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LocalCache } from "@skand/local-cache";
/**
 * Test if a file blob is a KML model
 * @param url
 * @param blob
 * @returns
 */
function isKmlFile(url, blob) {
    return __awaiter(this, void 0, void 0, function* () {
        const text = yield blob.text();
        return text.includes("<kml") || text.includes("<KML");
    });
}
/**
 * Test if a file blob is an Obj model
 * @param url
 * @param blob
 * @returns
 */
function isObjFile(url, blob) {
    return __awaiter(this, void 0, void 0, function* () {
        return url.toLowerCase().endsWith(".obj") || url.toLowerCase().endsWith(".mtl");
    });
}
/**
 * Test if a file blob is an IFC model
 * @param url
 * @param blob
 * @returns
 */
function isIFCFile(url, blob) {
    return __awaiter(this, void 0, void 0, function* () {
        const text = yield blob.text();
        return text.includes("ISO-10303-21;");
    });
}
/**
 * Our implementation of loading textures from remote endpoints.
 */
export class FileLoader {
    /**
     * Loads the file at the specified URL into an ArrayBuffer asynchronously
     * @param url
     * @param options
     */
    static loadArrayBuffer(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const blob = yield this.fetchResponse(url, options);
            return yield blob.arrayBuffer();
        });
    }
    /**
     * Loads the file at the specified URL into a Blob asynchronously
     * @param url
     * @param options
     */
    static loadBlob(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.fetchResponse(url, options);
        });
    }
    /**
     * Loads the file at the specified URL into a string asynchronously
     * @param url
     * @param options
     */
    static loadText(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchResponse(url, options);
            return yield response.text();
        });
    }
    /**
     * Loads the file at the specified URL and parse it as JSON asynchronously
     * @param url
     * @param options
     */
    static loadJson(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return JSON.parse(yield this.loadText(url, options));
        });
    }
    /**
     * Loads the file at the specified URL and parse it as a Document asynchronously
     * @param url
     * @param options
     */
    static loadDocument(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.fetchResponse(url, options);
            const text = yield response.text();
            return this._domParser.parseFromString(text, "text/xml");
        });
    }
    static fetchResponse(url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (FileLoader.CONFIGURATION.useLocalCache && ((options === null || options === void 0 ? void 0 : options.cache) || (options === null || options === void 0 ? void 0 : options.cacheKey))) {
                return yield this._localCache.fetch(url, options);
            }
            const init = {
                method: "GET",
            };
            if (typeof options !== "undefined" && typeof options.headers !== "undefined") {
                init.headers = options.headers;
            }
            const response = yield fetch(new Request(url, init));
            // Some browsers return HTTP Status 0 when using non-http protocol
            // e.g. 'file://' or 'data://'. Handle as success.
            if (response.status === 200 || response.status === 0) {
                return response.blob();
            }
            else {
                throw new Error(`GET request failed with status ${response.status}`);
            }
        });
    }
    static clearCache() {
        this._localCache.clear();
    }
}
FileLoader.CACHE_SIZE = 1024 * 1024 * 1024 * 4; // 4 GB
FileLoader._domParser = new DOMParser();
FileLoader._localCache = new LocalCache({
    name: "Renderer Cache",
    capacity: FileLoader.CACHE_SIZE,
    poolTypes: [
        {
            name: "KML",
            isMember: isKmlFile,
            allocationRatio: 0.2,
        },
        {
            name: "OBJ",
            isMember: isObjFile,
            allocationRatio: 0.4,
        },
        {
            name: "IFC",
            isMember: isIFCFile,
            allocationRatio: 0.4,
        },
    ],
});
